import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingCart, Tag, ArrowRight, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const { user, fetchCartCount, getCart, addToCart, updateGuestItem, removeGuestItem } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCart = async () => {
    try {
      const data = await getCart();
      setCart(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchCart(); }, [user]);

  const updateQty = async (itemId, qty) => {
    if (user) {
      try {
        const res = await axios.put(`${API}/cart/${itemId}`, { quantity: qty }, { withCredentials: true });
        setCart(res.data);
        fetchCartCount();
      } catch {}
    } else {
      updateGuestItem(itemId, qty);
      setCart(await getCart());
    }
  };

  const removeItem = async (itemId) => {
    if (user) {
      try {
        const res = await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
        setCart(res.data);
        fetchCartCount();
        toast.success("Item removed");
      } catch {}
    } else {
      removeGuestItem(itemId);
      setCart(await getCart());
      toast.success("Item removed");
    }
  };

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode, subtotal: cart.total }, { withCredentials: true });
      setCouponDiscount(res.data.discount);
      toast.success(`Coupon applied! Save Rs.${res.data.discount}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid coupon");
      setCouponDiscount(0);
    }
  };

  const mrpTotal = cart.items.reduce((sum, i) => sum + i.mrp * i.quantity, 0);
  const subtotal = cart.total;
  const delivery = subtotal >= 499 ? 0 : 49;
  const tax = Math.round(subtotal * 0.05 * 100) / 100;
  const finalTotal = Math.round((subtotal + delivery + tax - couponDiscount) * 100) / 100;

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-2xl" />)}</div></div>;

  if (cart.items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Your Cart is Empty</h2>
        <p className="text-slate-500 mb-6">Browse our products and add items to your cart</p>
        <Link to="/products"><Button className="rounded-full bg-cyan-600 hover:bg-cyan-700 px-8" data-testid="empty-cart-shop-btn">Start Shopping</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="cart-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Shopping Cart ({cart.items.length} items)</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map(item => (
            <div key={item.item_id} className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 flex gap-4" data-testid={`cart-item-${item.item_id}`}>
              <Link to={`/products/${item.product_id}`} className="w-20 h-20 rounded-xl overflow-hidden bg-slate-50 shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/products/${item.product_id}`} className="text-sm font-semibold text-slate-800 hover:text-cyan-600 line-clamp-1">{item.name}</Link>
                <p className="text-xs text-slate-500">{item.pack_size}</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold text-slate-800">Rs.{item.price}</span>
                    {item.mrp > item.price && <span className="text-xs text-slate-400 line-through">Rs.{item.mrp}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                      <button onClick={() => updateQty(item.item_id, Math.max(1, item.quantity - 1))} className="px-2 py-1 hover:bg-slate-50"><Minus className="w-3 h-3" /></button>
                      <span className="px-3 py-1 text-sm font-medium border-x border-slate-200">{item.quantity}</span>
                      <button onClick={() => updateQty(item.item_id, item.quantity + 1)} className="px-2 py-1 hover:bg-slate-50"><Plus className="w-3 h-3" /></button>
                    </div>
                    <button onClick={() => removeItem(item.item_id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-item-${item.item_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Apply Coupon</h3>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" className="pl-10 rounded-xl text-sm" data-testid="coupon-input" />
              </div>
              <Button onClick={applyCoupon} variant="outline" className="rounded-xl border-cyan-200 text-cyan-700" data-testid="apply-coupon-btn">Apply</Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">Try: WELCOME20, FLAT50, HEALTH10</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100" data-testid="order-summary">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-600"><span>MRP Total</span><span className="line-through">Rs.{mrpTotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>Rs.{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-emerald-600"><span>Discount</span><span>-Rs.{(mrpTotal - subtotal).toFixed(2)}</span></div>
              {couponDiscount > 0 && <div className="flex justify-between text-emerald-600"><span>Coupon Discount</span><span>-Rs.{couponDiscount.toFixed(2)}</span></div>}
              <div className="flex justify-between text-slate-600"><span>Delivery</span><span>{delivery === 0 ? <span className="text-emerald-600">FREE</span> : `Rs.${delivery}`}</span></div>
              <div className="flex justify-between text-slate-600"><span>GST (5%)</span><span>Rs.{tax.toFixed(2)}</span></div>
              <div className="border-t border-slate-100 pt-2 flex justify-between font-bold text-slate-800 text-base">
                <span>Total</span><span data-testid="cart-total">Rs.{finalTotal.toFixed(2)}</span>
              </div>
            </div>
            <Button onClick={() => { if (!user) { navigate("/login?redirect=/checkout"); toast.info("Please login to complete your purchase"); } else navigate("/checkout"); }}
              className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700 rounded-full py-3 font-semibold" data-testid="checkout-btn">
              Proceed to Checkout <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {subtotal < 499 && <p className="text-xs text-slate-400 mt-2 text-center">Add Rs.{(499 - subtotal).toFixed(0)} more for free delivery</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
